<template>
  <div
    class="disco-card"
    @click="flipCard(props.master_id)"
    @keydown.enter="flipCard(props.master_id)"
    tabindex="0"
    role="button"
  >
    <div class="album-cover-container">
      <img :src="props.cover_art_url" :alt="props.title" loading="lazy" class="album-cover">
      <div class="play-button">
        <svg viewBox="0 0 24 24" width="24" height="24" xmlns="http://www.w3.org/2000/svg">
          <path d="M8 5v14l11-7z" fill="currentColor"/>
        </svg>
      </div>
      <div class="album-overlay"></div>
    </div>
    <div class="card-content">
      <h3 class="card-title">{{ props.title }}</h3>
      <p class="card-artist">{{ props.artists.join(', ') }}</p>
      <div class="card-meta">
        <div v-if="genres.length" class="card-genres">
          <span class="genre-pill">{{ genres[0] }}</span>
          <span v-if="genres.length > 1" class="genre-pill more" :title="genres.slice(1).join(', ')">
            +{{ genres.length - 1 }}
          </span>
        </div>
        <div class="card-details">
          <span v-if="props.year" class="card-year">{{ props.year }}</span>
          <span v-if="props.labels && props.labels.length" class="card-label">
            {{ props.labels[0] }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<!-- possible iframe to use later-->
<!--<iframe id="ifrm" src="/ami.html" width="500" height="500"></iframe>-->

<script setup>
import { computed, onMounted } from 'vue';
import { getUriFromMaster } from '@/services/discogs';

const props = defineProps({
  title: String,
  id: Number,
  cover_art_url: String,
  genres: {
    type: Array,
    default: () => [],
  },
  year: Number,
  master_id: String,
  artists: {
    type: Array,
    default: () => [],
  },
  labels: {
    type: Array,
    default: () => [],
  },
});

// genres can arrive as null from older cached records, which the prop default doesn't cover
const genres = computed(() => props.genres || []);

// lifecycle hooks
onMounted(() => {
});

async function flipCard(masterId) {
  // Releases with no master (master_id 0) only have a release page
  if (!masterId || masterId === '0') {
    window.open(`https://www.discogs.com/release/${props.id}`, '_blank', 'noreferrer');
    return;
  }
  const { uri } = await getUriFromMaster(masterId);
  window.open(uri, '_blank', 'noreferrer');
}
</script>

<style scoped>
.disco-card {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
}

.disco-card:hover .play-button {
  opacity: 1;
  transform: translateY(0) scale(1);
}

.disco-card:hover .album-overlay {
  opacity: 0.5;
}

.album-cover-container {
  position: relative;
  width: 100%;
  padding-bottom: 100%; /* 1:1 Aspect Ratio */
  margin-bottom: 0.65rem;
  border-radius: var(--border-radius-sm);
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.35);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
}

.disco-card:hover .album-cover-container {
  transform: translateY(-4px);
  box-shadow: 0 12px 24px rgba(0, 0, 0, 0.5);
}

.album-cover {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.album-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
}

.disco-card:hover .album-cover {
  transform: scale(1.04);
}

.play-button {
  position: absolute;
  right: 10px;
  bottom: 10px;
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background-color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.4);
  opacity: 0;
  transform: translateY(10px) scale(0.9);
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  z-index: 2;
}

.play-button:hover {
  transform: scale(1.1) translateY(0);
  background-color: var(--color-primary-light);
}

.card-content {
  flex: 1;
  display: flex;
  flex-direction: column;
}

.card-title {
  font-size: 0.95rem;
  font-weight: 700;
  margin: 0 0 0.15rem 0;
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.3;
}

.card-artist {
  font-size: 0.875rem;
  font-weight: 400;
  margin: 0 0 0.25rem 0;
  color: var(--color-text-secondary);
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
}

.card-meta {
  margin-top: auto;
  padding-top: 0.4rem;
}

.card-genres {
  display: flex;
  flex-wrap: nowrap;
  gap: 0.25rem;
  margin-bottom: 0.3rem;
}

.genre-pill.more {
  flex-shrink: 0;
}

.genre-pill {
  min-width: 0;
  max-width: 100%;
  padding: 1px 8px;
  border-radius: 999px;
  background: var(--color-surface-lighter);
  color: var(--color-text-secondary);
  font-size: 0.7rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.card-details {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  font-size: 0.75rem;
  color: var(--color-text-secondary);
}

.card-year {
  font-weight: 500;
}

.card-label {
  max-width: 70%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Focus state for accessibility */
.disco-card:focus-visible {
  outline: 2px solid var(--color-primary);
  outline-offset: 2px;
}
</style>
